import React, { useState, useEffect, useRef } from "react";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { ButtonLoader } from "../../components";
import { Login2FAForm } from "./children";
import {
  POST_VERIFY_PHONE,
  POST_VERIFY_PHONE_CALL_CODE,
  POST_VERIFY_PHONE_CODE
} from "../../queries";
import { LoginWrapper } from "./LoginWrapper";
import { useTranslation, Trans } from "react-i18next";
import { CONSUMER_SESSION_AUTH } from "../../constants";
import { useAuthRedirect } from "../../hooks";
import { Alert } from "@material-ui/lab";

const Login2FA = () => {
  const history = useHistory();
  const { state = {} } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { getRedirectUrl, removeRedirectUrl } = useAuthRedirect();
  const isSmsVerify = useRef(true);

  const [formSubmitting, setFormSubmitting] = useState(false);
  const [showPhoneCallNotice, setShowPhoneCallNotice] = useState(false);

  /**
   * Post login
   */
  const [postVerifyPhone, { loading: smsCodeLoading }] = useMutation(
    POST_VERIFY_PHONE,
    {
      onError: () => {},
      onCompleted: () => {
        enqueueSnackbar("Code has been resent", { variant: "success" });
      }
    }
  );

  /**
   * Post verification phone call
   */

  const [postVerifyPhoneCallCode, { loading }] = useMutation(
    POST_VERIFY_PHONE_CALL_CODE,
    {
      onCompleted: ({ verifyPhoneCallCode }) => {
        try {
          const { status } = verifyPhoneCallCode;

          if (status === "success") {
            return setShowPhoneCallNotice(true);
          }
        } catch (error) {
          console.log(error);
        }
        return enqueueSnackbar("Unable to initiate verification phone call", {
          variant: "error"
        });
      }
    }
  );

  /**
   * Post verification code
   */
  const [postVerifyPhoneCode] = useMutation(POST_VERIFY_PHONE_CODE, {
    onError: () => {
      enqueueSnackbar("Unable to send code", { variant: "error" });
      setFormSubmitting(false);
    },
    onCompleted: ({ verifyPhoneCode }) => {
      const { status, token, session_token } = verifyPhoneCode;

      if (status === "success" && token) {
        sessionStorage.setItem(
          CONSUMER_SESSION_AUTH,
          JSON.stringify({
            phone_number: state.phone,
            token,
            session_token
          })
        );

        if (session_token) {
          history.push({
            pathname: "/login/verify",
            state: {
              phone: state.phone
            }
          });
        } else {
          history.push({
            pathname: getRedirectUrl()
          });

          removeRedirectUrl();
        }
      } else {
        enqueueSnackbar("Couldn't authenticate", { variant: "error" });
        setFormSubmitting(false);
      }
    }
  });

  /**
   * Handle submit form
   */
  const onSubmit = ({ code }) => {
    setFormSubmitting(true);
    postVerifyPhoneCode({
      variables: {
        body: {
          otp: code,
          phone_number: state.phone
        }
      }
    });
  };

  /**
   * Handle resending code
   */
  const handleResendCode = () => {
    const { phone, brand, has_sms } = state;

    if (isSmsVerify.current) {
      postVerifyPhone({
        variables: {
          body: {
            phone_number: phone,
            has_sms,
            brand
          }
        }
      });
    } else {
      postVerifyPhoneCallCode({
        variables: {
          phone_number: state.phone,
          body: {
            phone_number: state.phone
          }
        }
      });
    }
  };

  useEffect(() => {
    // If not required state, return to login
    if (!("has_sms" in state) || !("phone" in state) || !("brand" in state))
      return history.push("/login");

    if (state.has_sms === false) {
      isSmsVerify.current = false;

      postVerifyPhoneCallCode({
        variables: {
          phone_number: state.phone,
          body: {
            phone_number: state.phone
          }
        }
      });
    }
  }, [state]);

  return (
    <LoginWrapper showLogo={true}>
      <>
        <Login2FAForm onSubmit={onSubmit} formSubmitting={formSubmitting}>
          {showPhoneCallNotice && (
            <Box mb={2}>
              <Alert severity="info">
                <Trans i18nKey="auth.two_factor.code_by_phone">
                  We are now calling <strong>{{ phone: state.phone }}</strong>{" "}
                  to provide your authentication code.
                </Trans>
              </Alert>
            </Box>
          )}
        </Login2FAForm>
        <Box p={4}>
          <ButtonLoader
            data-cy="button-resend-2FAcode"
            fullWidth
            loading={smsCodeLoading || loading}
            onClick={e => handleResendCode()}
          >
            {t("auth.two_factor.button_resend")}
          </ButtonLoader>
        </Box>
      </>
    </LoginWrapper>
  );
};

export default Login2FA;
